// frontend/src/components/ArtTags/ArtTagPage.js
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import ArtworkCard from "../CategoryPage/ArtworkCard";
import LoadingSpinner from "../common/LoadingSpinner";
import "./ArtTagPage.css";

const ArtTagPage = () => {
  const { categoryId, tagId } = useParams();
  const navigate = useNavigate();

  const [category, setCategory] = useState(null);
  const [artTag, setArtTag] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const observerRef = useRef();
  const lastArtworkRef = useRef();

  // Load category and tag details
  useEffect(() => {
    const loadDetails = async () => {
      try {
        const [categoryData, tagData] = await Promise.all([
          api.getCategoryById(categoryId),
          api.getArtTagById(tagId),
        ]);
        setCategory(categoryData);
        setArtTag(tagData);
      } catch (err) {
        setError("Category or tag not found");
        console.error(err);
      }
    };
    loadDetails();
  }, [categoryId, tagId]);

  // Reset when category or tag changes
  useEffect(() => {
    setArtworks([]);
    setPage(1);
    setHasMore(true);
    setInitialLoading(true);
  }, [categoryId, tagId]);

  // Load artworks when page changes
  useEffect(() => {
    const loadArtworks = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.getArtworksByTag(categoryId, artTag?._id, page);
        setArtworks((prev) => [...prev, ...data.artworks]);
        setHasMore(data.hasMore);
      } catch (err) {
        setError("Failed to load artworks");
        console.error(err);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };

    // Only load artworks if we have the tag name
    if (artTag?.name) {
      loadArtworks();
    }
  }, [categoryId, artTag?.name, page]);

  // Infinite scroll observer
  useEffect(() => {
    if (loading || !hasMore) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPage((p) => p + 1);
      }
    });

    observerRef.current = observer;
    if (lastArtworkRef.current) {
      observer.observe(lastArtworkRef.current);
    }

    return () => observer.disconnect();
  }, [loading, hasMore]);

  const handleSelectArtwork = (artwork) => {
    const limit = 12;
    navigate(`/artwork/${artwork._id}`, {
      state: {
        artworkList: artworks,
        categoryId,
        tagId,
        categoryName: category?.name,
        tagName: artTag?.name,
        hasMore,
        currentPage: page,
        pageLimit: limit,
      },
    });
  };

   const handleBackToArtTags = () => {
     navigate(`/category/${category._id}/tags`);
   };

  if (initialLoading) {
    return <LoadingSpinner message="Loading artworks..." />;
  }

  return (
    <div className="arttag-page-main">
      <div className="arttag-page-content">
        {/* Breadcrumb Navigation */}
        <nav className="arttag-breadcrumb">
          <button
            className="arttag-breadcrumb-link"
            onClick={handleBackToArtTags}
            aria-label={`Back to ${category?.name}`}
          >
            <svg
              className="arttag-breadcrumb-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {category.name}
          </button>
          <span className="arttag-breadcrumb-separator">/</span>
          <span className="arttag-breadcrumb-current">
            {artTag?.name || "Loading..."}
          </span>
        </nav>
        {/* Header with Category and Tag */}
        <div className="arttag-page-header">
          <h1 className="arttag-page-title">
            {category?.name || "Loading..."}
          </h1>
          <div className="arttag-page-tag-badge">
            <span className="arttag-page-tag-label">Tag:</span>
            <span className="arttag-page-tag-name">
              {artTag?.name || "Loading..."}
            </span>
          </div>
        </div>

        {error && <div className="arttag-page-error">{error}</div>}

        <div className="artworks-grid">
          {artworks.map((artwork, idx) => (
            <ArtworkCard
              key={artwork._id}
              artwork={artwork}
              onClick={() => handleSelectArtwork(artwork)}
              ref={idx === artworks.length - 1 ? lastArtworkRef : null}
            />
          ))}
        </div>

        {loading && (
          <div className="arttag-page-loading">
            <LoadingSpinner />
          </div>
        )}

        {!loading && artworks.length === 0 && (
          <div className="arttag-page-empty">
            No artworks found with this tag.
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtTagPage;
